import axios from 'axios';
import { getToken } from './tokenUtils';
import { API_CONFIG } from './constants';

/**
 * API Service utility for making authenticated API calls
 * Follows the existing authentication patterns in the app
 */
class ApiService {
  constructor() {
    this.baseURL = API_CONFIG.BASE_URL;

    // Create axios instance with default config
    this.axiosInstance = axios.create({
      baseURL: this.baseURL,
      timeout: 30000, // 30 second timeout
      headers: {
        'Accept': '*/*',
        'Content-Type': 'application/json'
      }
    });

    // Add request interceptor to include auth token
    this.axiosInstance.interceptors.request.use(
      (config) => {
        const token = getToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Add response interceptor for error handling
    this.axiosInstance.interceptors.response.use(
      (response) => {
        return response;
      },
      (error) => {
        // Handle common error scenarios
        return Promise.reject(error);
      }
    );
  }

  /**
   * Ensure authentication token is available
   * @throws {Error} If no token is found
   */
  ensureAuthenticated() {
    const token = getToken();
    if (!token) {
      throw new Error('No authentication token found in cookie');
    }
    return token;
  }

  /**
   * Make a GET request
   * @param {string} endpoint - API endpoint (relative to base URL)
   * @param {object} config - Additional axios config
   * @returns {Promise} API response data
   */
  async get(endpoint, config = {}) {
    try {
      this.ensureAuthenticated();

      const response = await this.axiosInstance.get(endpoint, config);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Make a POST request
   * @param {string} endpoint - API endpoint (relative to base URL)
   * @param {object} data - Request payload
   * @param {object} config - Additional axios config
   * @returns {Promise} API response data
   */
  async post(endpoint, data = {}, config = {}) {
    try {
      this.ensureAuthenticated();

      const response = await this.axiosInstance.post(endpoint, data, config);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Make a PUT request
   * @param {string} endpoint - API endpoint (relative to base URL)
   * @param {object} data - Request payload
   * @param {object} config - Additional axios config
   * @returns {Promise} API response data
   */
  async put(endpoint, data = {}, config = {}) {
    try {
      this.ensureAuthenticated();

      const response = await this.axiosInstance.put(endpoint, data, config);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Make a DELETE request
   * @param {string} endpoint - API endpoint (relative to base URL)
   * @param {object} config - Additional axios config
   * @returns {Promise} API response data
   */
  async delete(endpoint, config = {}) {
    try {
      this.ensureAuthenticated();
      const response = await this.axiosInstance.delete(endpoint, config);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Make a PATCH request
   * @param {string} endpoint - API endpoint (relative to base URL)
   * @param {object} data - Request payload
   * @param {object} config - Additional axios config
   * @returns {Promise} API response data
   */
  async patch(endpoint, data = {}, config = {}) {
    try {
      this.ensureAuthenticated();
      const response = await this.axiosInstance.patch(endpoint, data, config);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Upload files with multipart/form-data
   * @param {string} endpoint - API endpoint (relative to base URL)
   * @param {FormData} formData - Form data containing files
   * @param {object} config - Additional axios config
   * @returns {Promise} API response data
   */
  async upload(endpoint, formData, config = {}) {
    try {
      this.ensureAuthenticated();

      const uploadConfig = {
        ...config,
        headers: {
          ...config.headers,
          'Content-Type': 'multipart/form-data'
        }
      };

      const response = await this.axiosInstance.post(endpoint, formData, uploadConfig);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Make a request with custom headers (useful for special cases)
   * @param {string} method - HTTP method
   * @param {string} endpoint - API endpoint
   * @param {object} data - Request payload
   * @param {object} customHeaders - Custom headers
   * @returns {Promise} API response data
   */
  async request(method, endpoint, data = {}, customHeaders = {}) {
    try {
      this.ensureAuthenticated();

      const url = endpoint;

      const config = {
        method: method.toLowerCase(),
        url,
        headers: customHeaders
      };

      if (['post', 'put', 'patch'].includes(method.toLowerCase())) {
        config.data = data;
      } else if (method.toLowerCase() === 'get') {
        config.params = data;
      }

      const response = await this.axiosInstance.request(config);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get the current base URL
   * @returns {string} Base URL
   */
  getBaseURL() {
    return this.baseURL;
  }

  /**
   * Update the base URL (useful for environment changes)
   * @param {string} newBaseURL - New base URL
   */
  setBaseURL(newBaseURL) {
    this.baseURL = newBaseURL;
    this.axiosInstance.defaults.baseURL = newBaseURL;
  }
}

// Create and export a singleton instance
const apiService = new ApiService();

/**
 * Domain-specific helpers
 */
/**
 * Get inspection templates with optional pagination support
 * @param {Object} options - Optional parameters
 * @param {number} [options.pageSize] - Number of items per page
 * @param {number} [options.pageIndex] - 0-based page index (requires pageSize)
 * @param {string} [options.searchTerm] - Optional search term to filter templates
 * @returns {Promise<PaginatedResponse<V2InspectionTemplateSummaryModel>>} Paginated template response
 */
export const getInspectionTemplates = ({ pageSize, pageIndex, searchTerm } = {}) => {
  const params = {};
  
  if (pageSize !== undefined && pageSize !== null) {
    params.pageSize = pageSize;
  }
  
  if (pageIndex !== undefined && pageIndex !== null) {
    params.pageIndex = pageIndex;
  }
  
  if (searchTerm && searchTerm.trim() !== '') {
    params.searchTerm = searchTerm.trim();
  }
  
  return apiService.get(API_CONFIG.INSPECTIONS.GET_TEMPLATES, { params });
};

export const getAllVehicles = (searchTerm = '', searchQuantity = 50) => {
  // Ensure searchTerm is a string and handle undefined/null
  const term = encodeURIComponent(searchTerm || '');
  // Ensure searchQuantity is a valid number, default to 50
  const quantity = Number.isInteger(searchQuantity) ? searchQuantity : 50;

  // Using route parameters as expected by the backend
  return apiService.get(`${API_CONFIG.VEHICLES.SEARCH_VEHICLE}/${term}/${quantity}`, {
    timeout: 10000 // 10 second timeout
  });
};

export const getInspectionTemplateCompanies = async (templateId) => {
  if (!templateId) throw new Error('Template ID is required');

  const data = await apiService.get(
    `${API_CONFIG.INSPECTIONS.GET_TEMPLATE_COMPANIES}/${templateId}`
  );

  return Array.isArray(data) ? data : [];
};


/**
 * Get company fleets with optional pagination and search support.
 * When pageSize is provided the API returns a paginated envelope:
 *   { items: VehicleModel[], totalCount: number, pageIndex: number, pageSize: number }
 * When omitted it returns a flat array (backward-compatible).
 *
 * @param {string} companyId - Company GUID
 * @param {Object} [options] - Optional pagination / search params
 * @param {number} [options.pageSize] - Page size for infinite-scroll
 * @param {number} [options.pageIndex] - 0-based page index (requires pageSize)
 * @param {string} [options.searchTerm] - Search term (matches registration, VIN, fleet, make, model)
 * @returns {Promise<Array|PaginatedResponse>}
 */
export const getCompanyFleets = async (companyId, { pageSize, pageIndex, searchTerm } = {}) => {
  try {
    if (!companyId) {
      throw new Error('No companyId provided to getCompanyFleets');
    }

    const normalizedCompanyId = String(companyId).trim();

    const endpoint = `${API_CONFIG.INSPECTIONS.GET_COMPANY_FLEETS}/${normalizedCompanyId}`;

    const params = {};
    if (pageSize !== undefined && pageSize !== null) {
      params.pageSize = pageSize;
    }
    if (pageIndex !== undefined && pageIndex !== null) {
      params.pageIndex = pageIndex;
    }
    if (searchTerm && searchTerm.trim() !== '') {
      params.searchTerm = searchTerm.trim();
    }

    const response = await apiService.get(endpoint, { params });

    // Paginated envelope – return as-is so the caller can read totalCount etc.
    if (params.pageSize !== undefined && response && !Array.isArray(response)) {
      return response;
    }

    // Flat array (backward-compatible)
    if (Array.isArray(response)) {
      return response;
    }

    if (response?.data && Array.isArray(response.data)) {
      return response.data;
    }

    return [];
  } catch (error) {
    throw error;
  }
};

export const getCompanyFleetTrucks = async (fleetNumber) => {
  try {
    const response = await apiService.get(`${API_CONFIG.INSPECTIONS.GET_COMPANY_FLEET_TRUCKS}/${fleetNumber}`);
    return response;
  } catch (error) {
    throw error;
  }
};

export const getVehicleByCompany = async (companyId) => {
  if (!companyId) {
    return [];
  }

  try {
    const response = await apiService.get(
      `${API_CONFIG.INSPECTIONS.GET_VEHICLE_BY_COMPANY}/${companyId}`
    );

    // Transform the response to handle different data structures
    const vehicles = Array.isArray(response) ? response :
      (response?.data ? (Array.isArray(response.data) ? response.data : [response.data]) : []);

    return vehicles.map(vehicle => ({
      ...vehicle,
      // Ensure vehicleModel properties are at the top level
      ...(vehicle.vehicleModel || {}),
      // Preserve the original ID
      id: vehicle.id || (vehicle.vehicleModel?.id || '')
    }));
  } catch (error) {
    return [];
  }
};
export const getInspectionTemplateById = (templateId) =>
  apiService.get(`${API_CONFIG.INSPECTIONS.GET_TEMPLATE}/${templateId}`);

export const getTruckByReg = (reg, id) => {
  const endpoint = `${API_CONFIG.INSPECTIONS.GET_TRUCK_BY_REG}/${reg}${id ? `/${id}` : ''}`;
  return apiService.get(endpoint);
};

export const createInspectionResponse = (payload) =>
  apiService.post(API_CONFIG.INSPECTIONS.RESPONSES, payload, { timeout: 30000 });

export const uploadResponseImages = (responseId, images) =>
  apiService.post(
    `${API_CONFIG.INSPECTIONS.UPLOAD_RESPONSE_IMAGES}/${responseId}/images`,
    images,
    { timeout: 120000 }
  );

// SAS direct upload — Step 1: get pre-signed upload URLs
export const generateImageUploadUrls = (responseId, items) =>
  apiService.post(
    `${API_CONFIG.INSPECTIONS.RESPONSES}/${responseId}/images/upload-urls`,
    items,
    { timeout: 30000 }
  );

// SAS direct upload — Step 3: confirm uploads and commit metadata
export const confirmImageUploads = (responseId, items) =>
  apiService.post(
    `${API_CONFIG.INSPECTIONS.RESPONSES}/${responseId}/images/confirm`,
    items,
    { timeout: 30000 }
  );

export const updateInspectionResponse = async (responseId, payload) => {
  try {
    const data = await apiService.put(`${API_CONFIG.INSPECTIONS.RESPONSES}/${responseId}`, payload, { timeout: 120000 });

    // Normalize empty responses (e.g., 204 No Content)
    const result = data ?? { status: 204 };

    return result;
  } catch (error) {
    throw error;
  }
};

export const getInspectionToPdf = async (responseId) => {
  try {
    const data = await apiService.get(`${API_CONFIG.INSPECTIONS.GET_INSPECTION_TO_PDF}/${responseId}`, { timeout: 0 });

    // Normalize empty responses (e.g., 204 No Content)
    const result = data ?? { status: 204 };

    return result;
  } catch (error) {
    throw error;
  }
};

/**
 * Get inspection response details with retry logic
 * @param {string} responseId - The ID of the inspection response
 * @param {boolean} includeImages - Whether to include images in the response
 * @param {boolean} useBlobUrls - If false returns the original base64 image, if true returns the blob URL
 * @param {number} maxRetries - Maximum number of retry attempts (default: 2)
 * @param {number} initialDelay - Initial delay between retries in ms (default: 1000)
 * @returns {Promise} A promise that resolves with the inspection response
 */
export const getInspectionResponseDetail = async (
  responseId,
  includeImages = false,
  useBlobUrls = false,
  maxRetries = 2,
  initialDelay = 1000
) => {
  if (!responseId) {
    throw new Error('Inspection response ID is required');
  }

  let lastError;
  let attempt = 0;

  while (attempt <= maxRetries) {
    try {
      const timeout = attempt === 0 ? 60000 : 30000;
      const endpoint = `${API_CONFIG.INSPECTIONS.RESPONSES}/${responseId}`;
      const params = { includeImages, useBlobUrls };

      const response = await apiService.get(endpoint, {
        params,
        timeout,
        // Add cache busting
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        }
      });

      return response;
    } catch (error) {
      lastError = error;
      attempt++;

      if (attempt > maxRetries) {
        throw error;
      }

      // Exponential backoff: wait longer between each retry
      const delay = initialDelay * Math.pow(2, attempt - 1);
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }

  throw lastError || new Error('Failed to fetch inspection response');
};

export const getMyInspectionResponses = ({ isCompleted, pageSize = 20, pageIndex = 0 } = {}) =>
  apiService.get(API_CONFIG.INSPECTIONS.GET_MY_RESPONSES, { params: { isCompleted, pageSize, pageIndex } });

export const deleteResponseImage = ({ blobPath, blobContainer } = {}) =>
  apiService.get(API_CONFIG.INSPECTIONS.DELETE_RESPONSE_IMAGE, { params: { blobPath, blobContainer } });

export const getInspectionHistory = ({
  pageSize,
  pageIndex,
  search,
  companyId
} = {}) => {
  const params = {};

  if (pageSize !== undefined && pageSize !== null) {
    params.pageSize = pageSize;
  }
  if (pageIndex !== undefined && pageIndex !== null) {
    params.pageIndex = pageIndex;
  }
  if (search && search.trim() !== '') {
    params.search = search.trim();
  }
  if (companyId && companyId.trim() !== '') {
    params.companyId = companyId.trim();
  }

  params.isCompleted = true;

  return apiService.get(API_CONFIG.INSPECTIONS.GET_HISTORY, { params });
};

export const getAccidentReportResponses = ({
  pageSize,
  pageIndex,
  isCompleted = true,
  searchTerm,
  createdDateFrom,
  createdDateTo,
  filter,
  filterDirection,
} = {}) => {
  const params = {};
  if (pageSize !== undefined && pageSize !== null) params.pageSize = pageSize;
  if (pageIndex !== undefined && pageIndex !== null) params.pageIndex = pageIndex;
  params.isCompleted = isCompleted;
  if (searchTerm && searchTerm.trim() !== '') params.searchTerm = searchTerm.trim();
  if (createdDateFrom) params.createdDateFrom = createdDateFrom;
  if (createdDateTo) params.createdDateTo = createdDateTo;
  if (filter) params.filter = filter;
  if (filterDirection) params.filterDirection = filterDirection;
  return apiService.get(API_CONFIG.INSPECTIONS.GET_ACCIDENT_REPORT_RESPONSES, { params });
};

export const getAccidentReport = async ({ pageSize, pageIndex, searchTerm } = {}) => {
  const params = {};
  if (pageSize !== undefined && pageSize !== null) params.pageSize = pageSize;
  if (pageIndex !== undefined && pageIndex !== null) params.pageIndex = pageIndex;
  if (searchTerm && searchTerm.trim() !== '') params.searchTerm = searchTerm.trim();
  try {
    const data = await apiService.get(API_CONFIG.INSPECTIONS.GET_ACCIDENT_REPORT, { params });
    if (!data) return null;
    return data;
  } catch {
    return null;
  }
};

//Inspections
export const getInspectionRequestDetails = (answerId) =>
  apiService.get(`${API_CONFIG.INSPECTIONS.GET_INSPECTION_REQUEST_DETAILS}/${answerId}`);

export const generateInspectionRequest = (data) =>
  apiService.post(`${API_CONFIG.BREAKDOWN_REQUESTS.POST_BREAKDOWN_REQUEST}`, data);

// Breakdown Requests
export const getActiveBreakdownRequests = () =>
  apiService.get(API_CONFIG.BREAKDOWN_REQUESTS.GET_ACTIVE);

// Supplier Mechanics
export const getSupplierMechanicList = () =>
  apiService.get(API_CONFIG.COMPANY.GET_MECHANIC_LIST);

export const assignMechanicAndSupplier = (
  breakdownId,
  { mechanicId, mechanicName, supplierId, companyId, companyName }
) =>
  apiService.post(
    `${API_CONFIG.BREAKDOWNS.ASSIGN_MECHANIC}/${breakdownId}`,
    {},
    {
      params: {
        mechanicId,
        mechanicName,
        supplierId,
        companyId,
        companyName,
        type: 'PWA'
      }
    }
  );

// Assigned mechanics for a breakdown (supplier view)
export const getAssignedMechanics = (breakdownId) =>
  apiService.get(`${API_CONFIG.BREAKDOWN_MECHANICS.GET_ASSIGNED}/${breakdownId}`);

// Call Me (Panics)
export const createCallMe = () =>
  apiService.post(API_CONFIG.PANICS.CREATE_CALL_ME);

export const getCallMeById = (callMeId) =>
  apiService.get(`${API_CONFIG.PANICS.GET_CALL_ME_BY_ID}/${callMeId}`);

// Panic
export const createPanic = (payload) =>
  apiService.post(API_CONFIG.PANICS.CREATE_PANIC, payload);

export const getPanicById = (panicId) =>
  apiService.get(`${API_CONFIG.PANICS.GET_PANIC_BY_ID}/${panicId}`);

// Breakdown History (Dashboard)
export const getBreakdownHistory = async (limit = 20) => {
  const data = await apiService.get(API_CONFIG.BREAKDOWNS.HISTORY_DASHBOARD_LIST, { params: { limit } });
  if (typeof data === 'string') {
    try {
      return JSON.parse(data);
    } catch {
      return [];
    }
  }
  return data;
};

// User
export const updateOnDuty = (isOnDuty) =>
  apiService.post(API_CONFIG.USER.SET_ON_DUTY, isOnDuty);

export const updateLocation = (location) =>
  apiService.post(API_CONFIG.USER.SET_LOCATION, location);

export const updateOldUserLocation = (location) =>
  apiService.post(API_CONFIG.USER.SET_OLD_USER_LOCATION, location);

export const getOnDuty = () =>
  apiService.get(API_CONFIG.USER.GET_ON_DUTY);

export { apiService };
export default apiService;
